import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { API } from '../api'
import Button from '../components/Button'
import { getUserState, setAppClients, useDispatch } from '../store'

interface DeleteCredLinkProps {
  id: string
  deleteMethod: (id: string) => void
}

function DeleteCredLink({ id, deleteMethod }: DeleteCredLinkProps) {
  return (
    <button
      type="button"
      className="colored-link font-normal"
      onClick={() => deleteMethod(id)}
    >
      Delete
    </button>
  )
}

export const APICredentials = () => {
  const dispatch = useDispatch()
  const { appClients } = useSelector(getUserState)

  const [selectedCreds, setSelectedCreds] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const deleteMethod = (cred: any) => {
    setSelectedCreds(cred)
    setIsDeleting(true)
  }

  const confirmDeletion = async () => {
    setIsProcessing(true)
    await API(await fetchAuthSession()).delete(
      `/app/${selectedCreds?.client_id}`
    )
    dispatch(
      setAppClients(
        appClients !== undefined
          ? appClients.filter(
              (cred) => cred.client_id !== selectedCreds?.client_id
            )
          : undefined
      )
    )
    setIsDeleting(false)
    setIsProcessing(false)
    setSelectedCreds(null)
  }

  const cancelDeletion = () => {
    setIsDeleting(false)
    setSelectedCreds(null)
  }

  return (
    <>
      <Dialog
        open={!!isDeleting}
        onClose={() => {
          setIsDeleting(false)
          setSelectedCreds(null)
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="mx-auto w-[50rem] rounded-lg p-20 bg-primary-50">
            <DialogTitle className="text-center text-[22px] mb-4">
              Delete credentials
            </DialogTitle>
            <Description className="py-4 text-center text-[18px] mb-4">
              Are you sure you wish to delete&nbsp;
              <span className="font-semibold">
                &quot;
                {selectedCreds?.app_name}
                &quot;
              </span>
              ?
            </Description>

            <div className="w-1/2 mx-auto">
              <div className="grid grid-cols-2 gap-2">
                <div className="w-full items-center">
                  <button
                    type="submit"
                    className="block w-full button button-large button-light py-2 px-4 my-2"
                    onClick={cancelDeletion}
                  >
                    <div className="button button-label">No, cancel</div>
                  </button>
                </div>
                <div className="w-full justify-center">
                  <button
                    type="submit"
                    className="block w-full button button-large button-dark py-2 px-4 my-2"
                    onClick={confirmDeletion}
                    disabled={isProcessing}
                  >
                    <div className="button button-label">Yes, delete</div>
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
      <div className="w-full flex mb-5 justify-between items-center">
        <h3 className="font-semibold text-[20px]">
          Active API Credentials
          {appClients !== undefined && ` - ${appClients.length}`}
        </h3>
        <Button
          as={Link}
          to="/api-credentials/create"
          label="Add new API Credentials"
          className="text-center"
        />
      </div>
      {appClients === undefined && (
        <div className="w-full rounded-xl border border-gray-400 p-8">
          Loading...
        </div>
      )}
      {appClients !== undefined && (
        <div className="w-full rounded-xl border border-gray-400 p-8">
          <table className="table-auto w-full border-spacing-3 access-token-table text-[14px]">
            <thead className="text-left font-normal">
              <tr>
                <th className="font-thin">Credentials name</th>
                <th className="font-thin">Client ID</th>
                <th className="font-thin">Created</th>
                <th className="font-thin">Last Used</th>
                <th className="font-thin">Expires</th>
                <th className="font-thin">Action</th>
              </tr>
            </thead>
            <tbody className="font-medium">
              {appClients !== undefined && appClients?.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    You do not have any API credentials.
                    <div className="w-60 mx-auto">
                      <Link to="/api-credentials/create">
                        <Button
                          label="Add new API Credentials"
                          className="w-60 my-4 text-center"
                        />
                      </Link>
                    </div>
                  </td>
                </tr>
              )}
              {appClients !== undefined &&
                appClients.map((cred) => (
                  <tr key={cred.client_id}>
                    <td>{cred.app_name}</td>
                    <td>{cred.client_id}</td>
                    <td>{new Date(cred.creation_date).toLocaleDateString()}</td>
                    <td>
                      {cred.last_time_used
                        ? new Date(cred.last_time_used).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td>Never</td>
                    <td>
                      <DeleteCredLink
                        id={cred.client_id}
                        deleteMethod={() => deleteMethod(cred)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
